// =====================================================================
//  PER-PAGE GUIDE VIDEOS
// =====================================================================
//  The "Guide" widget (film-strip pill on the right edge of every portal
//  page) opens a short MP4 walkthrough. THIS FILE decides WHICH video
//  plays on WHICH page.
//
//  HOW IT WORKS
//    • Each page maps to a video "basename" in the table below.
//    • The widget loads:   public/videos/guides/<basename>.mp4
//      (optional captions: public/videos/guides/<basename>.vtt,
//       optional poster:   public/videos/guides/<basename>-poster.jpg)
//
//  TO ADD OR REPLACE A PAGE'S VIDEO  (no code change for listed pages):
//    1. Export the page's walkthrough as an .mp4  (H.264 video + AAC audio
//       plays everywhere).
//    2. Name it <basename>.mp4 using the basename for that page below —
//       e.g. the New Order page uses "new-order", so "new-order.mp4".
//    3. Drop it in:   public/videos/guides/
//    4. (Optional) add captions: "<basename>.vtt" in the same folder.
//    5. Redeploy. Done.
//
//  TO RENAME A FILE OR ADD A BRAND-NEW PAGE:
//    Edit/add a line in GUIDE_VIDEOS below — the KEY is the page's route
//    NAME (the "Route name" column), the VALUE is the video basename.
//    A page that isn't listed automatically derives a basename from its
//    URL (slashes → dashes); if that file is missing too it falls back to
//    DEFAULT_GUIDE, and finally to a "coming soon" message — so nothing
//    ever shows a broken player.
//
//  ---------------------------------------------------------------------
//  Page (what the user sees)     URL                        Route name                        basename
//  ----------------------------- -------------------------- --------------------------------- ----------------------
//  Account Dashboard             /account                   Account Dashboard                 account
//  Account Information           /account/information       Account Information               account-information
//  Saved Carts (Orders/Quotes)   /account/savedcarts        Saved Carts                       saved-carts
//  Search Orders                 /account/search            SearchOrders                      search-orders
//  Order Details                 /account/orderdetails/:id  Order Details                     order-details
//  Quote Details                 /account/rfqdetails/:id    RFQ Details                       quote-details
//  New Order (step 1)            /orders/create             Create New Order                  new-order
//  New Order – Choose Assembly   /orders/chooseassembly     Create Order - Choose Assembly    order-choose-assembly
//  New Order – Shop Wear Parts   /orders/shopwearparts      Create Order - Shop Wear Parts    order-shop-wear-parts
//  New Order – Shop Assembly     /orders/shopassembly       Create Order - Shop Assembly      order-shop-assembly
//  New Quote (step 1)            /RFQ/create                Create New Quote                  new-quote
//  New Quote – Choose Assembly   /RFQ/chooseassembly        Create Quote - Choose Assembly    quote-choose-assembly
//  New Quote – Shop Wear Parts   /rfq/shopwearparts         Create Quote - Shop Wear Parts    quote-shop-wear-parts
//  New Quote – Shop Assembly     /RFQ/shopassembly          Create Quote - Shop Assembly      quote-shop-assembly
//  Review & Checkout (Cart)      /cart                      Cart                              cart
//  Finalize Order                /cart/finalize             Finalize Cart                     cart-finalize
//  Order Success                 /cart/success              Cart Success                      order-success
//  Review Quote                  /cart/rfqsaveditems        Quote Saved Items                 review-quote
//  Saved Quote                   /cart/savedrfq             Saved Quote                       saved-quote
//  Search                        /search                    Search                            search
//  Documents                     /documents                Documents                         documents
//  Contact                       /contact                   Contact                           contact
//  About                         /contact/about             About                             about
//  ---------------------------------------------------------------------
//
//  Keyed by ROUTE NAME (stable — unaffected by URL params or aliases).
export const GUIDE_VIDEOS = {
  "Account Dashboard": "account",
  "Account Information": "account-information",
  "Saved Carts": "saved-carts",
  SearchOrders: "search-orders",
  "Order Details": "order-details",
  "RFQ Details": "quote-details",
  "Create New Order": "new-order",
  "Create Order - Choose Assembly": "order-choose-assembly",
  "Create Order - Shop Wear Parts": "order-shop-wear-parts",
  "Create Order - Shop Assembly": "order-shop-assembly",
  "Create New Quote": "new-quote",
  "Create Quote - Choose Assembly": "quote-choose-assembly",
  "Create Quote - Shop Wear Parts": "quote-shop-wear-parts",
  "Create Quote - Shop Assembly": "quote-shop-assembly",
  Cart: "cart",
  "Finalize Cart": "cart-finalize",
  "Cart Success": "order-success",
  "Quote Saved Items": "review-quote",
  "Saved Quote": "saved-quote",
  Search: "search",
  Documents: "documents",
  Contact: "contact",
  About: "about",
};

// Shown on any page with no specific guide (and when a page's own video
// file isn't found). Put a general overview at
// public/videos/guides/how-it-works.mp4 — or set this to "" to show the
// "coming soon" placeholder instead of a fallback video.
export const DEFAULT_GUIDE = "how-it-works";

// =====================================================================
//  GUIDE GROUPS — pages that host SEVERAL walkthroughs (a chooser list)
// =====================================================================
//  Some pages are a shared entry point for more than one flow. The
//  New Order and New Quote nav links now BOTH land on the Search page
//  (/search?intent=order | ?intent=quote — see MainNav.navHref), so the
//  Search guide opens a CHOOSER: Placing an Order, Requesting a Quote, or
//  Searching for Parts. Keyed by ROUTE NAME (same as GUIDE_VIDEOS). When a
//  route appears here, the Guide widget shows the chooser instead of a
//  single video; arriving with ?intent auto-opens that walkthrough.
//
//  Each item:
//    key        : matches the ?intent value ("order" | "quote"); "search"
//                 is the no-intent default. Used to auto-open the flow the
//                 user arrived through.
//    label/blurb: shown in the chooser row.
//    basename   : which <basename>.mp4 to play (public/videos/guides/).
//    comingSoon : true → show a "coming soon" note instead of a video
//                 (until that walkthrough is produced). Drop the flag once
//                 the real <basename>.mp4 is in place.
export const GUIDE_GROUPS = {
  Search: [
    {
      key: "order",
      label: "New Order",
      blurb:
        "Build a purchase order from your machine's parts — add parts, set PO numbers, then review and place.",
      basename: "new-order",
      // Also offers a LIVE interactive walkthrough (drives the real page;
      // nothing is actually ordered). Handled by GuideTourWidget via the
      // 'start-guide-tour' root event. Value = the flow key in that widget.
      tour: "order",
    },
    {
      key: "quote",
      label: "New Quote",
      blurb:
        "Get pricing before you commit — add the parts you'd like quoted and save them as a request.",
      basename: "new-quote",
      // Customers still see "coming soon", but the tour flow exists so admins get
      // a "✎ Build / edit tour" button and can author it (e.g. copy the New Order
      // tour). Remove comingSoon once the New Quote tour is built + ready.
      tour: "quote",
      comingSoon: true,
    },
    {
      key: "search",
      label: "New Search",
      blurb:
        "Find any part across your equipment, then add it straight to an order or a quote.",
      basename: "search",
      tour: "search",
      comingSoon: true,
    },
  ],
};

// =====================================================================
//  GUIDE CATALOG — the global, chaptered "All Guides" menu
// =====================================================================
//  The GUIDE pill (every page) opens this organized catalog so a customer
//  can browse the walkthrough VIDEOS for each part of the portal from
//  anywhere. Sections → items → (optional) child chapters.
//
//  Each item:
//    key        : unique id within the catalog.
//    label/blurb: shown in the row.
//    basename   : which <basename>.mp4 plays (public/videos/guides/).
//    routeName  : the page this walkthrough is about (matches the Vue
//                 route NAME) — used to flag "You're here" + to scope the
//                 page's live tour / build button to the page you're on.
//    intent/tab : for pages that host several flows behind one route via a
//                 query (?intent on Search, ?tab on Saved Carts) — used for
//                 the "You're here" match only.
//    comingSoon : true → "Coming soon" badge instead of a playable video
//                 (per the rule: any page with no recorded video). Drop the
//                 flag once <basename>.mp4 exists.
//    children   : nested chapters (one level), e.g. the Quotes sub-flows.
//
//  Live interactive tours + the admin "Build / edit tour" button are PAGE-
//  contextual (the builder/player drive the real page), so they appear in
//  the "On this page" bar at the top of the menu — keyed to the page you're
//  currently on — NOT per catalog row.
export const GUIDE_CATALOG = [
  {
    title: "Dashboard",
    items: [
      { key: "dashboard", label: "Dashboard", blurb: "Your home base — recent orders, quotes, and quick links.", basename: "account", routeName: "Account Dashboard" },
    ],
  },
  {
    title: "Order & Quote",
    items: [
      { key: "order", label: "New Order", blurb: "Build a purchase order from your machine's parts — add parts, set PO numbers, then review and place.", basename: "new-order", routeName: "Search", intent: "order", tour: "order" },
      { key: "quote", label: "New Quote", blurb: "Get pricing before you commit — add the parts you'd like quoted and save them as a request.", basename: "new-quote", routeName: "Search", intent: "quote", tour: "quote" },
      { key: "search", label: "Search for Parts", blurb: "Find any part across your equipment, then add it to an order or a quote.", basename: "search", routeName: "Search", intent: "", tour: "search" },
    ],
  },
  {
    title: "Documents",
    items: [
      { key: "documents", label: "Documents", blurb: "Find drawings, manuals, and reference documents for your equipment.", basename: "documents", routeName: "Documents" },
    ],
  },
  {
    title: "Order History",
    items: [
      { key: "oh-orders", label: "Orders", blurb: "Review past orders and reorder in a click.", basename: "saved-carts", routeName: "Saved Carts", tab: "orders" },
      { key: "oh-saved", label: "Saved", blurb: "Pick up a saved cart where you left off.", basename: "saved-carts", routeName: "Saved Carts", tab: "saved", comingSoon: true },
      {
        key: "oh-quotes", label: "Quotes", blurb: "Track quote requests and turn them into orders.", basename: "saved-carts", routeName: "Saved Carts", tab: "quotes", comingSoon: true,
        children: [
          { key: "q-review", label: "Review a quote", blurb: "See the full detail of a quote request.", basename: "quote-details", routeName: "RFQ Details", comingSoon: true },
          { key: "q-create", label: "Create an order from a quote", blurb: "Turn an approved quote into a purchase order.", basename: "cart", routeName: "Cart" },
          { key: "q-cart", label: "Quotes Cart", blurb: "The parts staged in your quote request.", basename: "review-quote", routeName: "Quote Saved Items", comingSoon: true },
          { key: "q-final", label: "Finalized Quote", blurb: "Your completed, saved quote.", basename: "saved-quote", routeName: "Saved Quote", comingSoon: true },
        ],
      },
    ],
  },
];
